import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../lib/api';
import { AttachButton, UploadFeedback, useAttachmentUpload, useFileDrop } from './AttachmentUploader';
import { AttachmentPreview } from './AttachmentPreview';

export interface AttachmentRow {
  id_attachment: number;
  filename: string;
  original_filename: string;
  size_bytes: number;
  mime_type: string;
  sha256: string;
  uploaded_by_user: number;
  uploaded_at: string;
  av_status: 'skipped' | 'pending' | 'clean' | 'infected' | 'error';
}

const ICONS: Record<string, string> = {
  pdf: '📄',
  image: '🖼',
  zip: '🗜',
  word: '📝',
  excel: '📊',
  ppt: '📺',
  text: '📋',
  default: '📎',
};

function iconFor(mime: string): string {
  if (mime.startsWith('image/')) return ICONS['image']!;
  if (mime === 'application/pdf') return ICONS['pdf']!;
  if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar')) return ICONS['zip']!;
  if (mime.includes('wordprocess') || mime === 'application/msword') return ICONS['word']!;
  if (mime.includes('spreadsheet') || mime === 'application/vnd.ms-excel') return ICONS['excel']!;
  if (mime.includes('presentation') || mime === 'application/vnd.ms-powerpoint') return ICONS['ppt']!;
  if (mime.startsWith('text/')) return ICONS['text']!;
  return ICONS['default']!;
}

function humanSize(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

interface Props {
  displayId: number;
  /**
   * Bouton « Ajouter » et dépôt sur la liste. Faux quand la liste est dans la
   * zone de réponse, qui porte déjà le trombone et le dépôt.
   */
  canUpload?: boolean;
  /** Change quand une pièce a été ajoutée ailleurs (zone de réponse) : relit la liste. */
  refreshKey?: unknown;
}

/**
 * Pièces jointes du point en liste compacte (colonne à droite de la zone de
 * réponse) : mini-vignette ou icône, nom tronqué (nom complet en infobulle),
 * taille ; clic = aperçu. La liste défile dans sa colonne au lieu d'allonger
 * la page.
 */
export function AttachmentList({ displayId, canUpload = false, refreshKey }: Props) {
  const [items, setItems] = useState<AttachmentRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<AttachmentRow | null>(null);

  const reload = useCallback(() => {
    const ctl = new AbortController();
    apiGet<{ items: AttachmentRow[] }>(`/api/tickets/${displayId}/attachments`, ctl.signal)
      .then((d) => {
        if (ctl.signal.aborted) return;
        setItems(d.items);
        setErr(null);
      })
      .catch((e) => !ctl.signal.aborted && setErr(e instanceof Error ? e.message : 'Erreur'));
    return () => ctl.abort();
  }, [displayId]);

  useEffect(() => reload(), [reload, refreshKey]);

  const { busy, feedback, upload } = useAttachmentUpload(displayId, reload);
  const { dragging, dropProps } = useFileDrop((files) => void upload(files), canUpload && !busy);

  const count = items?.length;

  return (
    <section
      className={`attach-side${dragging ? ' is-dragging' : ''}`}
      aria-label="Pièces jointes du point"
      {...dropProps}
    >
      <div className="attach-side-head">
        <h4>Pièces jointes{count !== undefined && ` (${count})`}</h4>
        {canUpload && <AttachButton onFiles={(f) => void upload(f)} disabled={busy} label="Ajouter" />}
      </div>
      {err && <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      {count === 0 && <div className="attach-empty">Aucune pièce</div>}
      {items && items.length > 0 && (
        <ul className="attach-rows">
          {items.map((a) => (
            <li key={a.id_attachment}>
              <button
                type="button"
                className="attach-row"
                onClick={() => setPreview(a)}
                title={`${a.original_filename} (${humanSize(a.size_bytes)})`}
              >
                <span className="attach-mini" aria-hidden="true">
                  {a.mime_type.startsWith('image/') ? (
                    <img src={`/api/tickets/${displayId}/attachments/${encodeURIComponent(a.filename)}`} alt="" loading="lazy" />
                  ) : (
                    iconFor(a.mime_type)
                  )}
                </span>
                <span className="attach-row-text">
                  <span className="attach-row-name">{a.original_filename}</span>
                  <span className="attach-row-size">{humanSize(a.size_bytes)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {canUpload && <UploadFeedback busy={busy} feedback={feedback} />}
      {preview && <AttachmentPreview displayId={displayId} attachment={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}
